import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ApiRequestError, useApi } from "../api/client";
import { useCredentials, useInvalidateOrg } from "../api/queries";
import type { Credential, CredentialInput } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, Panel } from "../ui/Common";
import { Dialog } from "../ui/Dialog";
import { useToast } from "../ui/Toasts";
import { credentialsMessages } from "./credentials.i18n";

/** Aba Credenciais das configurações (ADR 0030): catálogo da organização, com onde cada uma é usada. */
export function CredentialsTab({ orgId }: { orgId: string }) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const q = useCredentials(orgId);
  const t = useT(credentialsMessages);
  const tc = useT(common);
  const f = useFormat();
  const [editing, setEditing] = useState<Credential | "new" | null>(null);
  const [removing, setRemoving] = useState<Credential | null>(null);
  const [busy, setBusy] = useState(false);

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await api.deleteCredential(orgId, removing.id);
      toast("ok", t.deleted);
      setRemoving(null);
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.actionFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      sub={t.intro}
      actions={<button className="btn btn-sm" type="button" onClick={() => setEditing("new")} disabled={!q.data}><Plus aria-hidden="true" /> {t.newCredential}</button>}
      bodyless
    >
      {q.isPending ? <LoadingRows rows={3} /> : q.isError ? <div className="panel-body"><ErrorState error={q.error} /></div> : q.data.length === 0 ? (
        <div className="empty"><KeyRound aria-hidden="true" />{t.none}</div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{t.colName}</th><th>{t.colUser}</th><th>{t.colUsedBy}</th><th>{t.colLastUsed}</th>
                <th><span className="sr-only">{t.colActions}</span></th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="host">
                      <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600 }}>{c.name}</strong>
                      <span>{t.kindSmb}</span>
                    </div>
                  </td>
                  <td className="mono">{c.domain ? `${c.domain}\\` : ""}{c.username}</td>
                  <td>{t.usedBy(c.usedBy)}</td>
                  <td className="num">{c.lastUsedAt ? f.ago(c.lastUsedAt) : t.neverUsed}</td>
                  <td className="right">
                    <div style={{ display: "inline-flex", gap: 2 }}>
                      <button className="btn btn-ghost btn-icon" type="button" aria-label={t.edit(c.name)} title={t.edit(c.name)} onClick={() => setEditing(c)}><Pencil aria-hidden="true" /></button>
                      <button className="btn btn-ghost btn-icon btn-danger" type="button" aria-label={t.deleteLabel(c.name)} title={t.deleteLabel(c.name)} onClick={() => setRemoving(c)}><Trash2 aria-hidden="true" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CredentialDialog orgId={orgId} credential={editing === "new" ? null : editing} onClose={(changed) => { setEditing(null); if (changed) invalidate(orgId); }} />
      )}

      <Dialog
        open={removing !== null}
        onOpenChange={(v) => { if (!v) setRemoving(null); }}
        title={removing ? t.deleteTitle(removing.name) : ""}
        description={t.deleteDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={remove} disabled={busy}>{t.delete}</button>
          </>
        }
      />
    </Panel>
  );
}

function CredentialDialog({ orgId, credential, onClose }: { orgId: string; credential: Credential | null; onClose: (changed: boolean) => void }) {
  const api = useApi();
  const toast = useToast();
  const t = useT(credentialsMessages);
  const tc = useT(common);
  const [name, setName] = useState(credential?.name ?? "");
  const [username, setUsername] = useState(credential?.username ?? "");
  const [domain, setDomain] = useState(credential?.domain ?? "");
  const [secret, setSecret] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const errors = {
    name: !name.trim() ? t.nameRequired : null,
    secret: !credential && !secret ? t.secretRequired : null,
  };
  const show = (k: keyof typeof errors) => (touched ? errors[k] : null);

  async function save(ev: React.FormEvent) {
    ev.preventDefault();
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    // Ausente mantém a senha guardada; só vai quando a pessoa digitou uma.
    const input: CredentialInput = { name: name.trim(), kind: "smb", username: username.trim(), domain: domain.trim(), ...(secret ? { secret } : {}) };
    setBusy(true);
    try {
      if (credential) await api.updateCredential(orgId, credential.id, input);
      else await api.createCredential(orgId, input);
      toast("ok", t.saved);
      onClose(true);
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : t.actionFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(false); }}
      title={credential ? t.edit(credential.name) : t.dialogNew}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onClose(false)}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="credential-form" disabled={busy}>{credential ? t.save : t.create}</button>
        </>
      }
    >
      <form id="credential-form" onSubmit={save} style={{ display: "grid", gap: 14 }} noValidate>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="cred-nome">{t.fieldName}</label>
            <input id="cred-nome" className="input" value={name} maxLength={80} placeholder={t.namePlaceholder} onChange={(e) => setName(e.target.value)} aria-invalid={Boolean(show("name"))} autoFocus />
            {show("name") && <span className="error">{show("name")}</span>}
          </div>
          <div className="field">
            <label htmlFor="cred-tipo">{t.fieldKind}</label>
            <input id="cred-tipo" className="input" value={t.kindSmb} disabled readOnly />
            <span className="hint">{t.kindFixed}</span>
          </div>
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="cred-usuario">{t.fieldUsername}</label>
            <input id="cred-usuario" className="input mono" value={username} maxLength={256} onChange={(e) => setUsername(e.target.value)} autoComplete="off" spellCheck={false} />
            <span className="hint">{t.usernameHint}</span>
          </div>
          <div className="field">
            <label htmlFor="cred-dominio">{t.fieldDomain}</label>
            <input id="cred-dominio" className="input mono" value={domain} maxLength={128} onChange={(e) => setDomain(e.target.value)} autoComplete="off" spellCheck={false} />
            <span className="hint">{t.domainHint}</span>
          </div>
        </div>
        <div className="field">
          <label htmlFor="cred-senha">{t.fieldSecret}</label>
          <input id="cred-senha" className="input" type="password" value={secret} maxLength={256} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" aria-invalid={Boolean(show("secret"))} />
          {show("secret") ? <span className="error">{show("secret")}</span> : credential ? <span className="hint">{t.secretKeep}</span> : null}
        </div>
      </form>
    </Dialog>
  );
}
